import { Box, Icon, Slider, SxProps, Typography } from "@outschool/backpack";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import {
  IconDefinition,
  faBusSchool,
  faCloudRainbow,
  faSun,
  faSunrise,
} from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import {
  DayOfWeekSelector,
  Filter,
  FilterList,
} from "@outschool/ui-components-website";
import {
  Input,
  Space,
  ToggleButton,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import startCase from "lodash/startCase";
import React from "react";

import exhaustiveCheck from "../../../shared/exhaustiveCheck";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import FilterActionButtons from "./desktop/FilterActionButtons";

interface QuickSelectProps {
  range: [number, number];
  icon: IconDefinition;
  label: string;
  enabled: boolean;
  onClick: (range: [number, number]) => void;
  trackingUniqueId: string;
  isDisabled?: boolean;
  timeRangeString?: string;
}

const QuickSelect = ({
  range,
  icon,
  label,
  enabled,
  onClick,
  trackingUniqueId: uniqueId,
  isDisabled,
  timeRangeString,
}: QuickSelectProps) => {
  const [rangeStart, rangeEnd] = range;

  const [impressionTrackingNode, setImpressionTrackingNode] =
    React.useState(null);
  const { trackTouch } = useImpressionTracking({
    node: impressionTrackingNode,
    trackingLabel: "time_filter_quick_link",
    uniqueId,
    sharedProperties: {
      label,
      rangeStart,
      rangeEnd,
    },
  });

  return (
    <ToggleButton
      sx={{ borderRadius: "large" }}
      enabled={enabled}
      disabled={isDisabled}
      ref={setImpressionTrackingNode}
      onClick={() => {
        trackTouch();
        onClick(range);
      }}
    >
      <Icon
        icon={icon}
        sx={{ color: enabled ? "common.white" : "primary.main" }}
      />
      <Box
        sx={{
          color: enabled ? "common.white" : "#575757",
          marginTop: "0.5em",
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          color: enabled ? "common.white" : "#6A7482",
          fontSize: 14,
          fontWeight: 300,
          marginTop: "0.5em",
        }}
      >
        {timeRangeString}
      </Box>
    </ToggleButton>
  );
};

export enum TIME_OF_DAY {
  ANYTIME = "Anytime",
  MORNING = "Morning",
  AFTERNOON = "Afternoon",
  EVENING = "Evening",
}

export const TIMES_OF_DAY = [
  {
    range: [Time.DAY_START_HOUR, Time.MIDNIGHT_HOUR],
    icon: faCloudRainbow,
    label: TIME_OF_DAY.ANYTIME,
    trackingUniqueId: "anytime",
  },
  {
    range: [7, 12],
    icon: faSunrise,
    label: TIME_OF_DAY.MORNING,
    trackingUniqueId: "morning",
  },
  {
    range: [12, 17],
    icon: faSun,
    label: TIME_OF_DAY.AFTERNOON,
    trackingUniqueId: "afternoon",
  },
  {
    range: [17, Time.MIDNIGHT_HOUR],
    icon: faBusSchool,
    label: TIME_OF_DAY.EVENING,
    trackingUniqueId: "after_school",
  },
];

function timeRangeString(timeOfDay: TIME_OF_DAY, t: TFunction) {
  switch (timeOfDay) {
    case TIME_OF_DAY.ANYTIME:
      return t("6am - 12am");
    case TIME_OF_DAY.MORNING:
      return t("7am - 12pm");
    case TIME_OF_DAY.AFTERNOON:
      return t("12pm - 5pm");
    case TIME_OF_DAY.EVENING:
      return t("5pm - 12am");
    default:
      throw exhaustiveCheck(timeOfDay);
  }
}

export function timeOfDayString(timeOfDay: TIME_OF_DAY, t: TFunction) {
  switch (timeOfDay) {
    case TIME_OF_DAY.ANYTIME:
      return t("Anytime");
    case TIME_OF_DAY.MORNING:
      return t("Morning");
    case TIME_OF_DAY.AFTERNOON:
      return t("Afternoon");
    case TIME_OF_DAY.EVENING:
      return t("Evening");
    default:
      throw exhaustiveCheck(timeOfDay);
  }
}

export function TimeOfDaySelector({
  value,
  onChange,
  timesOfDay,
  minTime,
  maxTime,
  step = 0.5,
  isDisabled = false,
  hidePreset = false,
  timeSelectorSx = {},
}: $TSFixMe) {
  const { t } = useTranslation("client\\components\\search\\TimeFilters");
  const [startAfterTime, endByTime] = value;
  return (
    <Box>
      {hidePreset ? null : (
        <Box
          sx={(theme: $TSFixMe) => ({
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",

            [theme.breakpoints.up("md")]: {
              gridTemplateColumns: "repeat(4, 1fr)",
            },

            gridGap: "0.5em",
          })}
        >
          {timesOfDay.map(
            ({
              range: [from, to],
              icon,
              label,
              trackingUniqueId,
            }: $TSFixMe) => (
              <QuickSelect
                key={label}
                range={[from, to]}
                icon={icon}
                label={timeOfDayString(label, t)}
                enabled={startAfterTime === from && endByTime === to}
                onClick={onChange}
                trackingUniqueId={trackingUniqueId}
                isDisabled={isDisabled}
                timeRangeString={timeRangeString(label, t)}
              />
            ),
          )}
        </Box>
      )}
      <Box
        sx={[
          {
            position: "relative",
            marginTop: "1em",
            marginBottom: "1em",
          },
          ...(Array.isArray(timeSelectorSx)
            ? timeSelectorSx
            : [timeSelectorSx]),
        ]}
      >
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            flexDirection: "row",
          }}
        >
          <Typography variant="inherit">
            {Time.dayjs.utc(startAfterTime * 60 * 60 * 1000).format("LT")}
          </Typography>
          <Typography variant="inherit">
            {Time.dayjs.utc(endByTime * 60 * 60 * 1000).format("LT")}
          </Typography>
        </Box>
        <Box
          sx={{
            marginTop: 16,
            paddingX: 16,
          }}
        >
          <Slider
            step={step}
            min={minTime}
            max={maxTime}
            value={value}
            onChange={(_e, values) => {
              const [startAfterTime, endByTime] = values as [number, number];
              const startChanged = startAfterTime !== value.startAfterTime;
              onChange([
                Math.max(
                  minTime,
                  startChanged
                    ? Math.min(startAfterTime, endByTime - step)
                    : startAfterTime,
                ),
                Math.min(
                  maxTime,
                  startChanged
                    ? endByTime
                    : Math.max(endByTime, startAfterTime + step),
                ),
              ]);
            }}
            disabled={isDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
}

type WeeklyMeetingsFilterProps = {
  filters: SearchFiltersType;
  onChange: (filters: SearchFiltersType) => void;
  hideTitle?: boolean;
  titleSx?: SxProps;
};

export function WeeklyMeetingsFilter({
  hideTitle,
  filters,
  onChange,
  titleSx,
}: WeeklyMeetingsFilterProps) {
  const { t } = useTranslation("client\\components\\search\\TimeFilters");

  return (
    <Filter title={hideTitle ? null : t("Weekly meetings")} titleSx={titleSx}>
      <Box flex sx={{ alignItems: "center" }}>
        <Input
          type="number"
          min={1}
          max={7}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.weeklyMeetingsMin ?? 0}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({
              ...filters,
              weeklyMeetingsMin: Number.isNaN(e.target.value)
                ? undefined
                : parseInt(e.target.value, 10),
            });
          }}
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle1">
          {t("to")}
        </Typography>
        <Input
          type="number"
          min={1}
          max={7}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.weeklyMeetingsMax ?? 7}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({
              ...filters,
              weeklyMeetingsMax: Number.isNaN(e.target.value)
                ? undefined
                : parseInt(e.target.value, 10),
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle1">
          {t("meetings per week")}
        </Typography>
      </Box>
    </Filter>
  );
}

type DurationWeeksFilterProps = {
  filters: SearchFiltersType;
  onChange: (filters: SearchFiltersType) => void;
  hideTitle?: boolean;
  titleSx?: SxProps;
};

function DurationWeeksFilter({
  hideTitle,
  filters,
  onChange,
  titleSx,
}: DurationWeeksFilterProps) {
  const { t } = useTranslation("client\\components\\search\\TimeFilters");

  return (
    <Filter title={hideTitle ? null : t("Class duration")} titleSx={titleSx}>
      <Box flex sx={{ alignItems: "center" }}>
        <Input
          type="number"
          min={1}
          max={16}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.durationWeeksMin ?? undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({
              ...filters,
              durationWeeksMin: Number.isNaN(e.target.value)
                ? undefined
                : parseInt(e.target.value, 10),
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle1">
          {t("to")}
        </Typography>
        <Input
          type="number"
          min={1}
          max={16}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.durationWeeksMax ?? undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({
              ...filters,
              durationWeeksMax: Number.isNaN(e.target.value)
                ? undefined
                : parseInt(e.target.value, 10),
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle1">
          {t("weeks long")}
        </Typography>
      </Box>
    </Filter>
  );
}

type TimeFiltersControlledProps = {
  value: SearchFiltersType;
  onChange: (filters: SearchFiltersType) => void;
  hideTitle?: boolean;
  titleSx?: SxProps;
  timeSelectorSx?: SxProps;
  isDisabled?: boolean;
  hideTimeOfDay?: boolean;
  hideDaySelector?: boolean;
  showGroupSelection?: boolean;
  timesOfDay?: Array<{
    range: number[];
    icon: IconDefinition;
    label: string;
    trackingUniqueId: string;
  }>;
  minTime?: number;
  maxTime?: number;
};

export function TimeFiltersControlled({
  value,
  onChange,
  hideTitle,
  titleSx,
  timeSelectorSx = {},
  isDisabled = false,
  hideTimeOfDay = false,
  hideDaySelector = false,
  showGroupSelection = true,
  timesOfDay = TIMES_OF_DAY,
  minTime = Time.DAY_START_HOUR,
  maxTime = Time.MIDNIGHT_HOUR,
}: TimeFiltersControlledProps) {
  const { t } = useTranslation("client\\components\\search\\TimeFilters");
  const { timeZone } = useTimeZone();

  const { startAfterTime, endByTime } = value;
  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  const timeRangeValues = [startAfterTime, endByTime].sort((a, b) => a - b);

  return (
    <FilterList>
      {!hideDaySelector && (
        <>
          <Filter
            title={hideTitle ? null : t("Day of the week")}
            titleSx={titleSx}
          >
            <DayOfWeekSelector
              value={value.dow}
              onChange={dow => onChange({ ...value, dow })}
              showGroupSelection={showGroupSelection}
              isDisabled={isDisabled}
            />
          </Filter>
        </>
      )}
      {!hideTimeOfDay && (
        <>
          <Filter
            title={
              hideTitle
                ? null
                : t("Time of day ({{timeZone}})", {
                    timeZone: startCase(Time.timeZoneName(timeZone)),
                  })
            }
            sx={{ marginLeft: "4px", marginRight: "4px" }}
            // @ts-ignore TS(2322): Type 'SxProps' is not assignable to type '{} | und... Remove this comment to see the full error message
            titleSx={titleSx}
          >
            <TimeOfDaySelector
              value={timeRangeValues}
              // @ts-ignore
              onChange={([startAfterTime, endByTime]) =>
                onChange({
                  ...value,
                  startAfterTime,
                  endByTime,
                })
              }
              timesOfDay={timesOfDay}
              minTime={minTime}
              maxTime={maxTime}
              isDisabled={isDisabled}
              hidePreset={hideTimeOfDay}
              // @ts-ignore TS(2322): Type 'SxProps' is not assignable to type '{} | und... Remove this comment to see the full error message
              timeSelectorSx={timeSelectorSx}
            />
          </Filter>
          <WeeklyMeetingsFilter
            hideTitle={hideTitle}
            titleSx={titleSx}
            onChange={onChange}
            filters={value}
          />
          <DurationWeeksFilter
            hideTitle={hideTitle}
            titleSx={titleSx}
            onChange={onChange}
            filters={value}
          />
        </>
      )}
    </FilterList>
  );
}

type TimeFiltersProps = {
  handleChangesExternally?: boolean;
  hideTimeOfDay?: boolean;
  onCancel: () => void;
  onChange: (filters: SearchFiltersType) => void;
  hideDaySelector?: boolean;
  showGroupSelection?: boolean;
  timeSelectorSx?: SxProps;
  titleSx?: SxProps;
  value: SearchFiltersType;
};

export default function TimeFilters({
  handleChangesExternally = false,
  hideTimeOfDay = false,
  hideDaySelector = false,
  onCancel,
  onChange,
  showGroupSelection = true,
  timeSelectorSx = {},
  titleSx = {},
  value,
}: TimeFiltersProps) {
  const [filters, setFiltersState] = React.useState<SearchFiltersType>({
    ...value,
    startAfterTime: value.startAfterTime || Time.DAY_START_HOUR,
    endByTime: value.endByTime || Time.MIDNIGHT_HOUR,
  });

  const handleFilterChange = (value: SearchFiltersType) => {
    setFiltersState(value);
    if (handleChangesExternally) {
      onChange(value);
    }
  };

  return (
    <>
      <TimeFiltersControlled
        hideDaySelector={hideDaySelector}
        hideTimeOfDay={hideTimeOfDay}
        onChange={handleFilterChange}
        showGroupSelection={showGroupSelection}
        timeSelectorSx={timeSelectorSx}
        titleSx={titleSx}
        value={filters}
      />
      {handleChangesExternally ? null : (
        <>
          <Space y="2em" />
          <FilterActionButtons
            onApply={() => onChange(filters)}
            onCancel={onCancel}
          />
        </>
      )}
    </>
  );
}
